import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  // Schema.org FAQPage structured data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>FAQ Division Immobilière | Questions Fréquentes | G Division</title>
        <meta name="description" content="Trouvez les réponses à vos questions sur la division immobilière : autorisations, coûts, délais, sortie d'indivision, succession. FAQ complète par G Division." />
        <meta name="keywords" content="FAQ division immobilière, questions division terrain, comment diviser maison, coût division immobilière, autorisation division" />
        <link rel="canonical" href="https://divisionimmobiliere.com/faq" />
        
        {/* Open Graph */}
        <meta property="og:title" content="FAQ Division Immobilière | G Division" />
        <meta property="og:description" content="Toutes les réponses à vos questions sur la division immobilière." />
        <meta property="og:url" content="https://divisionimmobiliere.com/faq" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Schema FAQPage - très puissant pour Google */}
      {faqs.length > 0 && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <div className="pt-20" data-testid="faq-page">
      {/* Hero */}
      <section className="bg-[#1e3a5f] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
              FAQ
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white mb-6">
              Questions Fréquentes
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              Trouvez les réponses aux questions les plus courantes sur la division immobilière, 
              l'indivision et nos services.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full loading-spinner"></div>
            </div>
          ) : faqs.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={faq.id} 
                  value={faq.id}
                  className="border border-gray-200 px-6 bg-white"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-['Playfair_Display'] text-lg font-semibold text-[#1e3a5f] hover:text-[#c9a227] py-6 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">Aucune FAQ disponible pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6">
            Une Autre Question ?
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed mb-10">
            N'hésitez pas à nous contacter pour toute question spécifique 
            concernant votre projet ou notre accompagnement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none px-10 py-4 uppercase tracking-wider text-sm font-semibold"
                data-testid="faq-contact-btn"
              >
                Nous Contacter
              </Button>
            </Link>
            <a 
              href="https://wa.me/33645040975"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="outline"
                className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-none px-10 py-4 uppercase tracking-wider text-sm font-semibold"
                data-testid="faq-whatsapp-btn"
              >
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default FAQ;
